import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const EXPERIENCE_COLORS = {
  Beginner: { bg: 'rgba(5, 150, 105, 0.12)', text: '#059669' },
  Intermediate: { bg: 'rgba(212, 165, 32, 0.12)', text: '#D4A520' },
  Advanced: { bg: 'rgba(11, 31, 59, 0.12)', text: '#0B1F3B' },
};

export default function MemberCard({ member, onFollow, onMessage, currentUserId }) {
  const navigate = useNavigate();
  const [following, setFollowing] = useState(member?.isFollowing || false);
  const [followLoading, setFollowLoading] = useState(false);

  const expColor = EXPERIENCE_COLORS?.[member?.experienceLevel] || EXPERIENCE_COLORS?.Beginner;

  const handleFollow = async (e) => {
    e?.stopPropagation();
    if (followLoading) return;
    setFollowLoading(true);
    const newState = !following;
    setFollowing(newState);
    try {
      await onFollow?.(member?.id, newState);
    } catch {
      setFollowing(!newState);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleMessage = (e) => {
    e?.stopPropagation();
    onMessage?.(member);
  };

  return (
    <div
      className="bg-white rounded-xl border cursor-pointer group card-hover overflow-hidden"
      style={{ borderColor: 'var(--color-border)' }}
      onClick={() => navigate(`/members/${member?.id}`)}
    >
      {/* Top accent bar */}
      <div className="h-1" style={{ background: 'linear-gradient(90deg, var(--color-primary), var(--color-accent))' }} />
      <div className="p-5">
        {/* Avatar + badges row */}
        <div className="flex items-start justify-between mb-4">
          <div className="relative">
            <div
              className="w-16 h-16 rounded-full overflow-hidden border-2 flex-shrink-0"
              style={{ borderColor: 'var(--color-accent)' }}
            >
              <Image
                src={member?.avatarUrl}
                alt={`${member?.fullName} profile photo`}
                className="w-full h-full object-cover"
              />
            </div>
            {member?.isMentor && (
              <div
                className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center shadow-sm"
                style={{ background: 'var(--color-accent)' }}
                title="Available as Mentor"
              >
                <Icon name="GraduationCap" size={12} color="var(--color-primary)" />
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <span
              className="px-2.5 py-0.5 rounded-full text-xs font-caption font-semibold"
              style={{ background: expColor?.bg, color: expColor?.text }}
            >
              {member?.experienceLevel}
            </span>
            {member?.isFoundingMember && (
              <span
                className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-caption font-medium"
                style={{ background: 'rgba(133,113,77,0.12)', color: 'var(--color-accent)' }}
              >
                <Icon name="Coins" size={10} color="var(--color-accent)" />
                Founding
              </span>
            )}
          </div>
        </div>

        {/* Name + title */}
        <h3
          className="font-heading font-semibold text-lg leading-tight mb-0.5 group-hover:text-accent transition-colors"
          style={{ color: 'var(--color-foreground)' }}
        >
          {member?.fullName}
        </h3>
        <p className="font-caption text-xs mb-1" style={{ color: 'var(--color-muted-foreground)' }}>
          {member?.title}
        </p>
        {member?.location && (
          <div className="flex items-center gap-1 mb-3">
            <Icon name="MapPin" size={11} color="var(--color-muted-foreground)" />
            <span className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
              {member?.location}
            </span>
          </div>
        )}

        {/* Bio */}
        <p
          className="font-body text-xs leading-relaxed mb-3 line-clamp-2"
          style={{ color: 'var(--color-muted-foreground)' }}
        >
          {member?.bio}
        </p>

        {/* Interest tags */}
        {member?.investmentInterests?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {member?.investmentInterests?.slice(0, 3)?.map((interest) => (
              <span
                key={interest}
                className="px-2 py-0.5 rounded-full text-xs font-caption"
                style={{ background: 'rgba(133,113,77,0.1)', color: 'var(--color-accent)', border: '1px solid rgba(133,113,77,0.25)' }}
              >
                {interest}
              </span>
            ))}
            {member?.investmentInterests?.length > 3 && (
              <span className="px-2 py-0.5 rounded-full text-xs font-caption" style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)' }}>
                +{member?.investmentInterests?.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="flex gap-4 pb-3 mb-3 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <div>
            <p className="font-data font-semibold text-sm" style={{ color: 'var(--color-primary)' }}>
              {member?.followersCount || 0}
            </p>
            <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Followers</p>
          </div>
          {member?.isWinVerified && (
            <div className="flex items-center gap-1">
              <Icon name="BadgeCheck" size={14} color="var(--color-accent)" />
              <span className="font-caption text-xs" style={{ color: 'var(--color-accent)' }}>WIN Verified</span>
            </div>
          )}
        </div>

        {/* Action buttons */}
        {currentUserId !== member?.id && (
          <div className="flex gap-2">
            <button
              onClick={handleFollow}
              disabled={followLoading}
              className="flex-1 flex items-center justify-center gap-1.5 h-8 rounded-md text-xs font-caption font-medium transition-all duration-200"
              style={following
                ? { background: 'rgba(0,48,87,0.08)', color: 'var(--color-primary)', border: '1px solid rgba(0,48,87,0.2)' }
                : { background: 'var(--color-accent)', color: 'var(--color-primary)' }
              }
            >
              <Icon name={following ? 'UserCheck' : 'UserPlus'} size={12} color="currentColor" />
              {following ? 'Following' : 'Follow'}
            </button>
            <button
              onClick={handleMessage}
              className="flex-1 flex items-center justify-center gap-1.5 h-8 rounded-md text-xs font-caption font-medium transition-all duration-200"
              style={{ border: '1px solid var(--color-primary)', color: 'var(--color-primary)', background: 'transparent' }}
            >
              <Icon name="MessageCircle" size={12} color="currentColor" />
              Message
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
